// src/pages/portal/PortalTicketDetallePage.tsx
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  usePortalTicket,
  usePortalComentarios,
  useAgregarPortalComentario,
} from '@/hooks/queries';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Skeleton,
  Textarea,
  Avatar,
} from '@/components/ui';
import { ArrowLeft, Send } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';

function TicketSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-32" />
      <Skeleton className="h-64" />
    </div>
  );
}

export default function PortalTicketDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [nuevoComentario, setNuevoComentario] = useState('');

  const { data: ticket, isLoading: loadingTicket, isError } = usePortalTicket(id);
  const { data: comentarios, isLoading: loadingComentarios } = usePortalComentarios(id);
  const agregarComentario = useAgregarPortalComentario();

  const handleEnviarComentario = () => {
    if (!nuevoComentario.trim() || !id) return;

    agregarComentario.mutate(
      { ticketId: id, data: { contenido: nuevoComentario } },
      {
        onSuccess: () => setNuevoComentario(''),
      }
    );
  };

  if (loadingTicket) {
    return <TicketSkeleton />;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error al cargar el ticket</p>
        <Button className="mt-4" onClick={() => navigate('/portal/tickets')}>
          Volver a tickets
        </Button>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">
          Ticket no encontrado
        </p>
        <Button className="mt-4" onClick={() => navigate('/portal/tickets')}>
          Volver a tickets
        </Button>
      </div>
    );
  }

  const estadoBadgeVariant = (estado: string) => {
    switch (estado?.toLowerCase()) {
      case 'abierto':
      case 'nuevo':
        return 'info';
      case 'en_progreso':
      case 'en progreso':
        return 'warning';
      case 'resuelto':
        return 'success';
      case 'cerrado':
        return 'default';
      default:
        return 'default';
    }
  };

  const prioridadBadgeVariant = (prioridad: string) => {
    switch (prioridad?.toLowerCase()) {
      case 'alta':
      case 'urgente':
        return 'danger';
      case 'media':
        return 'warning';
      case 'baja':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/portal/tickets')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>

      {/* Info del ticket */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  #{ticket.numero}
                </span>
                <Badge variant={estadoBadgeVariant(ticket.estado)}>
                  {ticket.estado}
                </Badge>
                <Badge variant={prioridadBadgeVariant(ticket.prioridad)}>
                  {ticket.prioridad}
                </Badge>
              </div>
              <CardTitle className="text-xl">{ticket.titulo}</CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Descripcion */}
          {ticket.descripcion && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descripcion
              </h4>
              <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                {ticket.descripcion}
              </p>
            </div>
          )}

          {/* Detalles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Tipo</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {ticket.tipo}
              </p>
            </div>
            {ticket.empresa && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Empresa</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {ticket.empresa.nombre}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Creado</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {format(new Date(ticket.fecha_creacion), 'dd/MM/yyyy HH:mm', { locale: es })}
              </p>
            </div>
            {ticket.fecha_actualizacion && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Actualizado</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDistanceToNow(new Date(ticket.fecha_actualizacion), {
                    addSuffix: true,
                    locale: es,
                  })}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Comentarios */}
      <Card>
        <CardHeader>
          <CardTitle>Comentarios</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Lista de comentarios */}
          {loadingComentarios ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : comentarios && comentarios.length > 0 ? (
            <div className="space-y-4">
              {comentarios.map((comentario) => (
                <div
                  key={comentario.id}
                  className={`p-4 rounded-lg ${
                    comentario.autor.es_soporte
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                      : 'bg-gray-50 dark:bg-gray-800'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Avatar
                      fallback={comentario.autor.nombre}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {comentario.autor.nombre}
                        </span>
                        {comentario.autor.es_soporte && (
                          <Badge variant="info">Soporte</Badge>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDistanceToNow(new Date(comentario.fecha), {
                            addSuffix: true,
                            locale: es,
                          })}
                        </span>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {comentario.contenido}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              No hay comentarios aun
            </p>
          )}

          {/* Nuevo comentario */}
          {ticket.estado !== 'cerrado' && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <Textarea
                placeholder="Escribe un comentario..."
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                rows={3}
              />
              <div className="flex justify-end mt-3">
                <Button
                  onClick={handleEnviarComentario}
                  disabled={!nuevoComentario.trim() || agregarComentario.isPending}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enviar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
